import { Button } from "../../components/Button";
import { useCopyToClipboard } from "../../hooks";
import { Clipboard, ClipboardCheck, Download01, } from "../../icons/v2-stroke";
import { cn } from "../../lib/utils";
import { cva } from "class-variance-authority";
import React, { useCallback } from "react";
const contentTypeExtensions = {
    "application/json": ".json",
    "text/csv": ".csv",
};
const wrapperVariants = cva("group relative w-full max-w-full min-w-0 overflow-visible", {
    variants: {
        buttonDisplay: {
            inside: "mt-0",
            outside: "mt-11",
        },
    },
    defaultVariants: {
        buttonDisplay: "outside",
    },
});
const actionsVariants = cva("absolute right-2 flex items-center gap-1 py-2", {
    variants: {
        buttonDisplay: {
            inside: "",
            outside: "bottom-0 h-11",
        },
        displayActions: {
            hover: "opacity-0 transition-opacity duration-200 group-hover:opacity-100",
            always: "",
        },
    },
    defaultVariants: {
        buttonDisplay: "outside",
        displayActions: "always",
    },
});
/**
 * Shared chrome for Markdown content blocks (code, tables, quotes): wraps its
 * children with an action bar providing copy-to-clipboard, optional download,
 * and any custom actions, positioned inside or above the block.
 * @summary Copy/download action wrapper for Markdown blocks.
 */
export function ContentBlockWrapper({ children, className, innerClassName, content, actions, displayActions = "always", getContentToDownload, buttonDisplay = "outside", }) {
    const [isCopied, copyToClipboard] = useCopyToClipboard();
    const handleCopyToClipboard = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        if (!content) {
            return;
        }
        const rawContent = typeof content === "string" ? { "text/plain": content } : content;
        // Replace invisible non-breaking spaces with regular spaces.
        if (rawContent["text/plain"]) {
            rawContent["text/plain"] = rawContent["text/plain"]
                .replaceAll("\xa0", " ")
                .trim();
        }
        const data = new ClipboardItem(Object.entries(rawContent).reduce((acc, [type, data]) => {
            acc[type] = new Blob([data], { type });
            return acc;
        }, {}));
        void copyToClipboard(data);
    }, [content, copyToClipboard]);
    const handleDownload = useCallback(async (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (!getContentToDownload) {
            return;
        }
        const { content, filename, type } = await getContentToDownload();
        const blob = new Blob([content], { type });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${filename}.${contentTypeExtensions[type]}`;
        a.click();
    }, [getContentToDownload]);
    return (React.createElement("div", { className: cn(wrapperVariants({ buttonDisplay }), className) },
        buttonDisplay !== null && (React.createElement("div", { className: "relative z-[1] h-0" },
            React.createElement("div", { id: "BlockActions", className: actionsVariants({ buttonDisplay, displayActions }) },
                actions && actions,
                getContentToDownload && (React.createElement(Button, { variant: "outline", size: "xs", icon: Download01, onClick: handleDownload, tooltip: "Download" })),
                content && (React.createElement(Button, { variant: "outline", size: "xs", icon: isCopied ? ClipboardCheck : Clipboard, onClick: handleCopyToClipboard, tooltip: "Copy" }))))),
        React.createElement("div", { className: cn("z-0 w-full min-w-0", innerClassName) }, children)));
}
//# sourceMappingURL=ContentBlockWrapper.js.map